import React, { FC } from 'react';
import { Button, IconButton, Typography } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useLocation, useNavigate } from 'react-router-dom';

import './coe-header.component.css';
import './coe-teams-header-v2.component.css';
import COETabsComponent from '../general/coe-tabs/coe-tabs.component';
import COETabComponent from '../general/coe-tab/coe-tab.component';
import { TeamMemberCounts } from '../../models/team.models';

export interface Tab {
  label: string;
  value: string;
  hide?: boolean;
}

export interface COETeamsHeaderV2Props {
  teamName: string;
  teamImage?: string | null;
  role?: string;
  memberCounts?: TeamMemberCounts;
  backLocation?: string;
  /** Called instead of navigating to backLocation when provided (e.g. to clear persisted team). */
  onBack?: () => void;
  loading?: boolean;
  params?: string;
  tabs?: Tab[];
  teamId?: string;
  /** When false, only the tab bar is shown (non-dashboard pages). Default true. */
  showHero?: boolean;
  /** Opens Manage Team modal instead of navigating to team-management */
  onManageTeam?: () => void;
  /** Hide Manage Team button (e.g. guardian supervised teams) */
  hideManageTeam?: boolean;
}

const COETeamsHeaderV2Component: FC<COETeamsHeaderV2Props> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    props.params ? navigate(`${newValue}?${props.params}`) : navigate(newValue);
  };

  const getValue = () => {
    if (location.pathname.includes('team-management')) {
      let path = location.pathname;
      const lastIndex = path.lastIndexOf('team-management');
      if (lastIndex !== -1) {
        path = path.substring(0, lastIndex + 'team-management'.length);
      }
      return path;
    }

    if (location.pathname.includes('athlete-schedules')) {
      let path = location.pathname;
      const lastIndex = path.lastIndexOf('athlete-schedules');
      if (lastIndex !== -1) {
        path = path.substring(0, lastIndex + 'athlete-schedules'.length);
      }
      return path;
    }

    return location.pathname;
  };

  const roleLabel = props.role ? props.role.charAt(0).toUpperCase() + props.role.slice(1) : '';
  const counts = props.memberCounts;
  const showHero = props.showHero !== false;

  const tabBar = (
    <nav className="teams-header-v2-tab-bar" aria-label="Team sections">
      <div className="teams-header-v2-tab-bar-inner">
        <IconButton
          className="teams-header-v2-back"
          style={{
            visibility: (props.backLocation || props.onBack) && !props.loading ? 'visible' : 'hidden',
          }}
          onClick={() => {
            if (props.onBack) {
              props.onBack();
              return;
            }
            if (props.backLocation) {
              navigate(props.backLocation);
            }
          }}
          aria-label="Back to teams"
        >
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
        <COETabsComponent value={getValue()} onChange={handleChange} variant="segmented">
          {props.tabs
            ?.filter((tab: Tab) => !tab.hide)
            .map((tab: Tab) => (
              <COETabComponent label={tab.label} key={tab.value} value={tab.value} variant="segmented" />
            ))}
        </COETabsComponent>
      </div>
    </nav>
  );

  return (
    <div className={`teams-header-v2${showHero ? '' : ' teams-header-v2-compact'}`}>
      {showHero ? (
        <div className="teams-header-v2-hero">
          {tabBar}
          <div className="teams-header-v2-hero-inner">
            <div className="teams-header-v2-identity">
              <div className="teams-header-v2-avatar">
                {props.teamImage ? (
                  <img src={props.teamImage} alt="" className="teams-header-v2-avatar-img" />
                ) : (
                  <GroupsOutlinedIcon className="teams-header-v2-avatar-icon" />
                )}
              </div>
              <div className="teams-header-v2-info">
                <Typography component="h1" className="teams-header-v2-name">
                  {props.loading ? '…' : props.teamName}
                </Typography>
                {roleLabel ? <span className="teams-header-v2-role">{roleLabel}</span> : null}
              </div>
            </div>

            <div className="teams-header-v2-stats">
              <div className="teams-header-v2-stat">
                <div className="teams-header-v2-stat-num">{counts?.totalCount ?? '—'}</div>
                <div className="teams-header-v2-stat-lbl">Members</div>
              </div>
              <div className="teams-header-v2-stat teams-header-v2-stat-green">
                <div className="teams-header-v2-stat-num">{counts?.athleteCount ?? '—'}</div>
                <div className="teams-header-v2-stat-lbl">Athletes</div>
              </div>
              <div className="teams-header-v2-stat teams-header-v2-stat-blue">
                <div className="teams-header-v2-stat-num">{counts?.coachCount ?? '—'}</div>
                <div className="teams-header-v2-stat-lbl">Coaches</div>
              </div>
              <div className="teams-header-v2-stat teams-header-v2-stat-amber">
                <div className="teams-header-v2-stat-num">{counts?.guardianCount ?? '—'}</div>
                <div className="teams-header-v2-stat-lbl">Guardians</div>
              </div>
              {props.teamId && !props.hideManageTeam ? (
                <Button
                  className="teams-header-v2-manage"
                  onClick={() => (props.onManageTeam ? props.onManageTeam() : undefined)}
                >
                  Manage Team
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        tabBar
      )}
    </div>
  );
};

export default COETeamsHeaderV2Component;
